import { forwardRef, type HTMLAttributes, type ReactElement } from 'react';

import { cn } from '../lib/cn.js';

/**
 * Card composto (padrão shadcn) — container com 5 sub-componentes para
 * compor cabeçalho, título, descrição, conteúdo e rodapé.
 *
 * @example
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Agendamentos hoje</CardTitle>
 *     <CardDescription>12 consultas confirmadas</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     <ul>...</ul>
 *   </CardContent>
 *   <CardFooter>
 *     <Button>Ver agenda</Button>
 *   </CardFooter>
 * </Card>
 */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function Card(
  { className, ...props },
  ref,
): ReactElement {
  return (
    <div
      ref={ref}
      className={cn('rounded-lg border bg-card text-card-foreground shadow-sm', className)}
      {...props}
    />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref): ReactElement {
    return <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />;
  },
);

export const CardTitle = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardTitle({ className, ...props }, ref): ReactElement {
    return (
      <div
        ref={ref}
        className={cn('text-lg font-semibold leading-none tracking-tight', className)}
        {...props}
      />
    );
  },
);

export const CardDescription = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardDescription({ className, ...props }, ref): ReactElement {
    return <div ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />;
  },
);

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref): ReactElement {
    return <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />;
  },
);

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref): ReactElement {
    return <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />;
  },
);
